import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';

// ============================================================
// Types
// ============================================================
interface ProfileAccessGroup {
  id: string;
  name: string;
  description: string | null;
  canView: boolean;
  canExport: boolean;
  canEdit: boolean;
  memberCount: number;
  createdAt: string;
}

interface GroupMember {
  memberId: string;
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  jobTitle: string | null;
  department: string | null;
  avatar: string | null;
}

interface UserForPicker {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  jobTitle: string | null;
  department: string | null;
}

interface UserWithProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  jobTitle: string | null;
  department: string | null;
  avatar: string | null;
  hasProfile: boolean;
  profileId: string | null;
  skills: string[];
  projectCount: number;
  certificationCount: number;
  experienceCount: number;
}

// ============================================================
// Styles (inline)
// ============================================================
const cardStyle: React.CSSProperties = {
  background: 'var(--color-bg-card)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  padding: '1.25rem',
  marginBottom: '1rem',
};

const badgeStyle = (active: boolean, color: string): React.CSSProperties => ({
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: '999px',
  fontSize: '0.7rem',
  fontWeight: 600,
  background: active ? color + '22' : 'var(--color-bg-hover)',
  color: active ? color : 'var(--color-text-muted)',
  border: `1px solid ${active ? color + '44' : 'transparent'}`,
});

const btnPrimary: React.CSSProperties = {
  padding: '0.5rem 1.25rem',
  background: 'var(--color-primary)',
  color: 'white',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  fontSize: '0.875rem',
  fontWeight: 600,
  cursor: 'pointer',
};

const btnSecondary: React.CSSProperties = {
  padding: '0.4rem 0.875rem',
  background: 'transparent',
  color: 'var(--color-text)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: '0.8rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const btnDanger: React.CSSProperties = {
  ...btnSecondary,
  color: 'var(--color-danger, #dc2626)',
  borderColor: 'var(--color-danger, #dc2626)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: '0.875rem',
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
  marginBottom: '0.25rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

// ============================================================
// GroupDialog
// ============================================================
interface GroupFormData { name: string; description: string; canView: boolean; canExport: boolean; canEdit: boolean; }
const emptyGroupForm: GroupFormData = { name: '', description: '', canView: true, canExport: false, canEdit: false };

function GroupDialog({ group, onClose }: { group: ProfileAccessGroup | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<GroupFormData>(
    group ? { name: group.name, description: group.description ?? '', canView: group.canView, canExport: group.canExport, canEdit: group.canEdit } : emptyGroupForm
  );

  const mutation = useMutation({
    mutationFn: (data: GroupFormData) =>
      group
        ? api.patch(`/profile-access/groups/${group.id}`, data).then((r) => r.data)
        : api.post('/profile-access/groups', data).then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['profile-access-groups'] });
      onClose();
    },
  });

  const overlayStyle: React.CSSProperties = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  const dialogStyle: React.CSSProperties = {
    background: 'var(--color-bg-card)', borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-lg)', padding: '1.5rem', width: 420, maxWidth: '95vw',
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={dialogStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 1.25rem', fontSize: '1rem', fontWeight: 700 }}>
          {group ? 'Gruppe bearbeiten' : 'Neue Gruppe'}
        </h3>

        <div style={{ marginBottom: '0.875rem' }}>
          <label style={labelStyle}>Gruppenname *</label>
          <input style={inputStyle} value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="z.B. HR-Team" />
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label style={labelStyle}>Beschreibung</label>
          <input style={inputStyle} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} placeholder="Optionale Beschreibung" />
        </div>

        <div style={{ marginBottom: '1.25rem' }}>
          <label style={labelStyle}>Berechtigungen</label>
          {(['canView', 'canExport', 'canEdit'] as const).map((key) => (
            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', cursor: 'pointer', fontSize: '0.875rem' }}>
              <input type="checkbox" checked={form[key]} onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.checked }))} />
              {{ canView: 'Ansehen', canExport: 'Exportieren', canEdit: 'Bearbeiten' }[key]}
            </label>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
          <button style={btnSecondary} onClick={onClose}>Abbrechen</button>
          <button style={btnPrimary} disabled={!form.name.trim() || mutation.isPending} onClick={() => mutation.mutate(form)}>
            {mutation.isPending ? 'Speichern…' : 'Speichern'}
          </button>
        </div>
        {mutation.isError && <p style={{ color: 'var(--color-danger, #dc2626)', fontSize: '0.8rem', marginTop: '0.5rem' }}>Fehler beim Speichern</p>}
      </div>
    </div>
  );
}

// ============================================================
// GroupCard
// ============================================================
function GroupCard({ group, allUsers }: { group: ProfileAccessGroup; allUsers: UserForPicker[] }) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [pickerUserId, setPickerUserId] = useState('');

  const { data: members = [] } = useQuery<GroupMember[]>({
    queryKey: ['profile-access-group-members', group.id],
    queryFn: () => api.get<GroupMember[]>(`/profile-access/groups/${group.id}/members`).then((r) => r.data),
    enabled: expanded,
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/profile-access/groups/${group.id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['profile-access-groups'] }),
  });

  const addMemberMutation = useMutation({
    mutationFn: (userId: string) => api.post(`/profile-access/groups/${group.id}/members`, { userId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['profile-access-group-members', group.id] });
      void qc.invalidateQueries({ queryKey: ['profile-access-groups'] });
      setPickerUserId('');
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => api.delete(`/profile-access/groups/${group.id}/members/${userId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['profile-access-group-members', group.id] });
      void qc.invalidateQueries({ queryKey: ['profile-access-groups'] });
    },
  });

  const memberUserIds = new Set(members.map((m) => m.id));
  const availableUsers = allUsers.filter((u) => !memberUserIds.has(u.id));

  return (
    <>
      {editing && <GroupDialog group={group} onClose={() => setEditing(false)} />}
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.4rem' }}>
              <span style={{ fontWeight: 700, fontSize: '0.9375rem' }}>{group.name}</span>
              <span style={badgeStyle(group.canView, '#2563eb')}>Ansehen</span>
              <span style={badgeStyle(group.canExport, '#7c3aed')}>Exportieren</span>
              <span style={badgeStyle(group.canEdit, '#059669')}>Bearbeiten</span>
            </div>
            {group.description && <p style={{ margin: '0 0 0.25rem', fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>{group.description}</p>}
            <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{group.memberCount} Mitglied{group.memberCount !== 1 ? 'er' : ''}</span>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
            <button style={btnSecondary} onClick={() => setExpanded((v) => !v)}>{expanded ? 'Schließen' : 'Mitglieder'}</button>
            <button style={btnSecondary} onClick={() => setEditing(true)}>Bearbeiten</button>
            <button style={btnDanger} disabled={deleteMutation.isPending} onClick={() => { if (confirm(`Gruppe "${group.name}" löschen?`)) deleteMutation.mutate(); }}>Löschen</button>
          </div>
        </div>

        {expanded && (
          <div style={{ marginTop: '1rem', borderTop: '1px solid var(--color-border)', paddingTop: '0.875rem' }}>
            {members.length === 0 ? (
              <p style={{ margin: '0 0 0.75rem', fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>Keine Mitglieder</p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
                {members.map((m) => (
                  <span key={m.memberId} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', background: 'var(--color-bg-hover)', border: '1px solid var(--color-border)', borderRadius: '999px', padding: '3px 10px', fontSize: '0.8rem' }}>
                    {m.firstName} {m.lastName}
                    <button onClick={() => removeMemberMutation.mutate(m.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', fontSize: '0.875rem', padding: 0, lineHeight: 1 }}>×</button>
                  </span>
                ))}
              </div>
            )}
            {availableUsers.length > 0 && (
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <select value={pickerUserId} onChange={(e) => setPickerUserId(e.target.value)}
                  style={{ ...inputStyle, width: 'auto', flex: 1 }}>
                  <option value="">Benutzer hinzufügen…</option>
                  {availableUsers.map((u) => (
                    <option key={u.id} value={u.id}>{u.firstName} {u.lastName} ({u.email})</option>
                  ))}
                </select>
                <button style={btnPrimary} disabled={!pickerUserId || addMemberMutation.isPending}
                  onClick={() => pickerUserId && addMemberMutation.mutate(pickerUserId)}>
                  Hinzufügen
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ============================================================
// ProfileStatusBadge
// ============================================================
function ProfileStatus({ user }: { user: UserWithProfile }) {
  if (!user.hasProfile) return <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>Kein Profil</span>;
  const complete = user.projectCount > 0 && user.skills.length > 0;
  return (
    <span style={{ fontSize: '0.75rem', fontWeight: 500, color: complete ? '#16a34a' : '#d97706' }}>
      {complete ? '✓ Vollständig' : '⚠ Unvollständig'}
    </span>
  );
}

// ============================================================
// Main Page
// ============================================================
export function AdminProfileAccessPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'groups' | 'profiles'>('groups');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [profileSearch, setProfileSearch] = useState('');

  const { data: groups = [], isLoading: groupsLoading } = useQuery<ProfileAccessGroup[]>({
    queryKey: ['profile-access-groups'],
    queryFn: () => api.get<ProfileAccessGroup[]>('/profile-access/groups').then((r) => r.data),
  });

  const { data: allUsers = [] } = useQuery<UserForPicker[]>({
    queryKey: ['profile-access-users'],
    queryFn: () => api.get<UserForPicker[]>('/profile-access/users').then((r) => r.data),
  });

  const { data: profileUsers = [], isLoading: profilesLoading } = useQuery<UserWithProfile[]>({
    queryKey: ['expert-profile-admin-users'],
    queryFn: () => api.get<UserWithProfile[]>('/expert-profile/admin/users').then((r) => r.data),
    enabled: activeTab === 'profiles',
  });

  const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: '0.5rem 1rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    borderBottom: `2px solid ${active ? 'var(--color-primary)' : 'transparent'}`,
    color: active ? 'var(--color-primary)' : 'var(--color-text-secondary)',
  });

  const filteredProfiles = profileUsers.filter((u) => {
    const q = profileSearch.toLowerCase();
    return (
      !q ||
      u.firstName.toLowerCase().includes(q) ||
      u.lastName.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q) ||
      (u.department ?? '').toLowerCase().includes(q)
    );
  });

  const handleDownload = async (userId: string, format: 'pdf' | 'docx', name: string) => {
    const url = `/api/v1/expert-profile/admin/users/${userId}/export/${format}`;
    const res = await api.get(url, { responseType: 'blob' });
    const blob = new Blob([res.data as BlobPart]);
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${name}_CV.${format}`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div style={{ maxWidth: 900 }}>
      {showCreateDialog && <GroupDialog group={null} onClose={() => setShowCreateDialog(false)} />}

      <div style={{ display: 'flex', borderBottom: '1px solid var(--color-border)', marginBottom: '1.5rem', gap: '0.25rem' }}>
        <button style={tabStyle(activeTab === 'groups')} onClick={() => setActiveTab('groups')}>Gruppen</button>
        <button style={tabStyle(activeTab === 'profiles')} onClick={() => setActiveTab('profiles')}>Profile</button>
      </div>

      {/* ── Gruppen-Tab ── */}
      {activeTab === 'groups' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Berechtigungsgruppen</h2>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                Gruppen mit Zugriff auf alle Expertenprofile verwalten
              </p>
            </div>
            <button style={btnPrimary} onClick={() => setShowCreateDialog(true)}>+ Neue Gruppe</button>
          </div>

          {groupsLoading ? (
            <p style={{ color: 'var(--color-text-secondary)' }}>Lädt…</p>
          ) : groups.length === 0 ? (
            <div style={{ ...cardStyle, textAlign: 'center', padding: '2.5rem', color: 'var(--color-text-secondary)' }}>
              <p style={{ margin: 0 }}>Noch keine Gruppen angelegt.</p>
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.8125rem' }}>Erstelle eine Gruppe, um Benutzern Zugriff auf alle Expertenprofile zu gewähren.</p>
            </div>
          ) : (
            groups.map((g) => <GroupCard key={g.id} group={g} allUsers={allUsers} />)
          )}
        </div>
      )}

      {/* ── Profile-Tab ── */}
      {activeTab === 'profiles' && (
        <div>
          <div style={{ marginBottom: '1.25rem' }}>
            <h2 style={{ margin: '0 0 0.25rem', fontSize: '1rem', fontWeight: 700 }}>Alle Expertenprofile</h2>
            <p style={{ margin: '0 0 0.875rem', fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
              Ansehen, exportieren und bearbeiten
            </p>
            <input
              style={{ ...inputStyle, maxWidth: 320 }}
              placeholder="Suchen nach Name, E-Mail, Abteilung…"
              value={profileSearch}
              onChange={(e) => setProfileSearch(e.target.value)}
            />
          </div>

          {profilesLoading ? (
            <p style={{ color: 'var(--color-text-secondary)' }}>Lädt…</p>
          ) : (
            <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--color-bg-hover)', borderBottom: '1px solid var(--color-border)' }}>
                    {['Name', 'Abteilung', 'Profil-Status', 'Aktionen'].map((h) => (
                      <th key={h} style={{ padding: '0.625rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredProfiles.map((u, idx) => (
                    <tr key={u.id} style={{ borderBottom: idx < filteredProfiles.length - 1 ? '1px solid var(--color-border)' : 'none' }}>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                          {u.avatar ? (
                            <img src={u.avatar} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                          ) : (
                            <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.75rem', fontWeight: 700, flexShrink: 0 }}>
                              {u.firstName[0]}{u.lastName[0]}
                            </div>
                          )}
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{u.firstName} {u.lastName}</div>
                            {u.jobTitle && <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>{u.jobTitle}</div>}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                        {u.department ?? '—'}
                      </td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        <ProfileStatus user={u} />
                      </td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
                          <button style={btnSecondary} title="Profil bearbeiten" onClick={() => navigate(`/admin/profiles/${u.id}`)}>Bearbeiten</button>
                          {u.hasProfile && (
                            <>
                              <button style={btnSecondary} title="PDF herunterladen" onClick={() => void handleDownload(u.id, 'pdf', `${u.firstName}_${u.lastName}`)}>PDF</button>
                              <button style={btnSecondary} title="Word herunterladen" onClick={() => void handleDownload(u.id, 'docx', `${u.firstName}_${u.lastName}`)}>Word</button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredProfiles.length === 0 && (
                    <tr><td colSpan={4} style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>Keine Ergebnisse</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
